import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, ShieldCheck, Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { ERA_LABELS } from "@/lib/taxonomy";

export default function Brands() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [following, setFollowing] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!user) { setFollowing([]); return; }
    api.get("/brands/following").then((r) => setFollowing(r.data.map((f) => f.brand_key))).catch(() => {});
  }, [user]);

  const toggle = async (name) => {
    if (!user) { nav("/auth"); return; }
    const key = name.toLowerCase();
    try {
      if (following.includes(key)) {
        await api.delete(`/brands/follow/${encodeURIComponent(name)}`);
        setFollowing((f) => f.filter((k) => k !== key));
        toast.success(`Stopped watching ${name}`);
      } else {
        await api.post("/brands/follow", { brand: name });
        setFollowing((f) => [...f, key]);
        toast.success(`Watching ${name} — new arrivals land in your Saved tab`);
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const shown = useMemo(
    () => brands.filter((b) => b.name.toLowerCase().includes(q.trim().toLowerCase())),
    [brands, q]
  );
  const stocked = shown.filter((b) => b.count > 0);
  const archive = shown.filter((b) => b.count === 0);

  const Card = ({ b }) => {
    const watched = following.includes(b.name.toLowerCase());
    return (
      <div className="group hairline card-lift bg-white flex flex-col justify-between min-h-[150px]"
        data-testid={`brand-card-${b.name}`}>
        <Link to={`/browse?brand=${encodeURIComponent(b.name)}`} className="block p-5 pb-3">
          <div className="label-eyebrow">{ERA_LABELS[b.era] || "Heritage"}</div>
          <div className="mt-2 font-display text-xl tracking-tight">{b.name}</div>
          <p className="mt-1 text-xs text-neutral-500 leading-relaxed">{b.note}</p>
        </Link>
        <div className="px-5 pb-4 flex items-center justify-between gap-2">
          <Link to={`/browse?brand=${encodeURIComponent(b.name)}`}
            className={`text-[0.66rem] uppercase tracking-[0.18em] ${b.count ? "text-black" : "text-neutral-400"}`}
            data-testid={`brand-count-${b.name}`}>
            {b.count ? `${b.count} listing${b.count > 1 ? "s" : ""}` : "None listed yet"}
          </Link>
          <div className="flex items-center gap-2">
            {b.verified > 0 && (
              <span className="inline-flex items-center gap-1 text-[0.66rem]" style={{ color: "#16A34A" }}>
                <ShieldCheck size={12} /> {b.verified}
              </span>
            )}
            <button onClick={() => toggle(b.name)} data-testid={`follow-${b.name}`}
              title={watched ? "Stop watching" : "Alert me when one lands"}
              className="inline-flex items-center gap-1 px-2 py-1.5 border text-[0.6rem] uppercase tracking-[0.14em] transition-colors"
              style={watched
                ? { background: "#2563EB", color: "#fff", borderColor: "#2563EB" }
                : { borderColor: "#DFDCD6", color: "#1A201A" }}>
              {watched ? <BellRing size={11} /> : <Bell size={11} />} {watched ? "Watching" : "Watch"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="brands-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="label-eyebrow">Heritage Directory</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Brands.</h1>
          <p className="mt-3 text-sm text-neutral-500 max-w-xl">
            The labels that defined the 80s, 90s and Y2K. Every branded listing is AI-screened for authenticity
            before it appears — replicas must say so. Tap <span className="text-black">Watch</span> and we'll tell you
            the moment one lands.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3.5 text-neutral-400" />
          <input data-testid="brand-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Find a brand" className="hairline pl-9 pr-3 py-3 text-sm w-[260px] bg-white" />
        </div>
      </div>

      {following.length > 0 && (
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="watching-row">
          <span className="label-eyebrow" style={{ color: "#2563EB" }}>Watching</span>
          {brands.filter((b) => following.includes(b.name.toLowerCase())).map((b) => (
            <button key={b.name} className="chip" data-active="true" onClick={() => toggle(b.name)}
              data-testid={`unwatch-${b.name}`}>{b.name} ×</button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-10 label-eyebrow">Loading…</div>
      ) : (
        <>
          {stocked.length > 0 && (
            <section className="mt-10">
              <div className="label-eyebrow">In stock now</div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="brands-stocked">
                {stocked.map((b) => <Card key={b.name} b={b} />)}
              </div>
            </section>
          )}
          <section className="mt-14">
            <div className="label-eyebrow">The archive — wanted list</div>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="brands-archive">
              {archive.map((b) => <Card key={b.name} b={b} />)}
            </div>
            {archive.length === 0 && stocked.length === 0 && (
              <div className="hairline p-16 text-center mt-4">
                <div className="label-eyebrow">No brand matches “{q}”</div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
